import React, { Fragment } from "react";
import PropTypes from "prop-types";
import { Alert } from "reactstrap";
import { Formik, Form } from "formik";
import {
  faPaperPlane,
  faArrowRight,
  faEdit,
  faHandPaper,
  faWrench,
  faSyncAlt
} from "@fortawesome/fontawesome-free-solid";
import FontAwesomeIcon from "@fortawesome/react-fontawesome";
import TextInput from "./TextInput";
import SelectCustomerButton from "./SelectCustomerButton";
import Customer from "./Customer";
import Scooter from "./Scooter";
import SelectScooterButton from "./SelectScooterButton";
import { isEmpty } from "lodash";
import Field from "./PreventEnterField";
import { pick, difference } from "lodash";
import CustomerButton from "./CustomerButton";
import ScooterButton from "./ScooterButton";
import {
  continueMessages,
  onChangeCheckbox,
  getBreakpointFromWidth
} from "../helpers/index";
import Button from "./CustomButton";
import Checkbox from "./CustomCheckbox";
import {
  branch,
  renderNothing,
  compose,
  withProps,
  setDisplayName
} from "recompose";
import SelectInput from "./SelectInput";
import { statuses, transformLowdash } from "../helpers";
import WithReziseListener from "../containers/WithResizeListener/index";
import * as Yup from "yup";
import { PrimaryTabs, SecondaryTabs } from "./ContractDetailed";
import { withRouter } from "react-router-dom";

const states = statuses.map(status => ({
  value: status,
  label: transformLowdash(status)
}));

const getId = ({ id }) => id;

// const Events = compose(
//   withProps(({ currentContract }) => ({
//     events: currentContract ? currentContract.events : []
//   }))
// )(EventSection);

const initialValues = currentContract =>
  currentContract
    ? pick(currentContract, ["labor_rate", "warranty", "key_tag", "aasm_state", "repair_order_no"])
    : { labor_rate: "", warranty: "", key_tag: "", repair_order_no: "" };

const withHoldProps = displayName =>
  compose(
    setDisplayName(displayName),
    branch(
      ({ role, currentContract }) =>
        !currentContract ||
        !(role === "staff" && currentContract.aasm_state === "in_service"),
      renderNothing
    ),
    withProps(
      ({
        submittingContract,
        currentContract: { aasm_state },
        confirmedItems,
        completedItems
      }) => ({
        disabled:
          submittingContract ||
          (["in_service"].includes(aasm_state) &&
            completedItems.length >= confirmedItems.length)
      })
    )
  );

const ResumeButton = compose(
  setDisplayName("ResumeButton"),
  branch(
    ({ role, currentContract }) =>
      !currentContract ||
      !(
        role === "staff" &&
        ["hold", "waiting_for_parts"].includes(currentContract.aasm_state)
      ),
    renderNothing
  ),
  withProps(
    ({
      submittingContract,
      currentContract,
      confirmedItems,
      completedItems
    }) => ({
      disabled:
        submittingContract ||
        (currentContract &&
          ["in_service"].includes(currentContract.aasm_state) &&
          completedItems.length >= confirmedItems.length)
    })
  )
)(({ onClickTransition, disabled, values }) => (
  <div className="col-md-auto col-sm-12 px-md-1 mb-3">
    <Button
      type="button"
      fill
      block
      disabled={disabled}
      onClick={onClickTransition("resume")(values)}
      color="primary"
    >
      Mark As In Service <FontAwesomeIcon icon={faSyncAlt} />
    </Button>
  </div>
));

const OnHoldButton = withHoldProps("OnHoldButton")(
  ({ onClickTransition, disabled, values }) => (
    <div className="col-md-auto col-sm-12 px-md-1 mb-3">
      <Button
        type="button"
        fill
        block
        disabled={disabled}
        onClick={onClickTransition("hold")(values)}
        color="primary"
      >
        Mark As On Hold <FontAwesomeIcon icon={faHandPaper} />
      </Button>
    </div>
  )
);

const WaitingForPartsButton = withHoldProps("WaitingForPartsButton")(
  ({ onClickTransition, disabled, values }) => (
    <div className="col-md-auto col-sm-12 px-md-1 mb-3">
      <Button
        type="button"
        fill
        block
        disabled={disabled}
        onClick={onClickTransition("waiting_parts")(values)}
        color="primary"
      >
        Mark As Waiting For Parts <FontAwesomeIcon icon={faWrench} />
      </Button>
    </div>
  )
);

const ContinueButton = compose(
  setDisplayName("ContinueButton"),
  branch(
    ({ role, currentContract }) =>
      !currentContract ||
      !(
        (role === "staff" &&
          !["completed", "hold", "waiting_for_parts"].includes(
            currentContract.aasm_state
          )) ||
        (role === "customer" &&
          currentContract.aasm_state === "customer_confirmation_needed")
      ),
    renderNothing
  ),
  withProps(
    ({
      submittingContract,
      currentContract,
      confirmedItems,
      requiredItems,
      completedItems
    }) => ({
      disabled:
        !currentContract ||
        submittingContract ||
        ([
          "in_diagnostic",
          "approve_diagnostic",
          "customer_confirmation_needed"
        ].includes(currentContract.aasm_state) &&
          isEmpty(requiredItems)) ||
        (["customer_confirmation_needed"].includes(
          currentContract.aasm_state
        ) &&
          difference(requiredItems.map(getId), confirmedItems.map(getId))
            .length !== 0) ||
        (["in_service"].includes(currentContract.aasm_state) &&
          completedItems.length < confirmedItems.length),
      continueMessage: continueMessages[currentContract.aasm_state]
    })
  )
)(({ onClickTransition, disabled, continueMessage, values }) => (
  <div className="col-md-auto col-sm-12 px-md-1 mb-3">
    <Button
      type="button"
      fill
      block
      disabled={disabled}
      onClick={onClickTransition("continue")(values)}
      color="primary"
    >
      {continueMessage} <FontAwesomeIcon icon={faArrowRight} />
    </Button>
  </div>
));

const ButtonSection = ({ separator, ...props }) => (
  <Fragment>
    {separator && <hr />}
    <ContinueButton {...props} />
    <OnHoldButton {...props} />
    <WaitingForPartsButton {...props} />
    <ResumeButton {...props} />
  </Fragment>
);

const ContractForm = compose(
  setDisplayName("ContractForm"),
  WithReziseListener,
  withRouter
)(props => {
  const {
    onSubmit,
    submittingContract,
    failSubmitContract,
    currentCustomer,
    currentScooter,
    currentContract,
    items,
    setCurrentScooter,
    width
  } = props;
  const breakpoint = getBreakpointFromWidth(width);
  const isSmallScreen = ["xs", "sm"].includes(breakpoint);
  return (
    <Formik
      enableReinitialize
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      initialValues={initialValues(currentContract)}
      render={({ values, setValues, submitForm }) => {
        const buttons = (
          <div className="row">
            <div className="col-md-auto col-sm-12 mb-3 pr-md-1">
              <Button
                block
                onClick={submitForm}
                type="button"
                disabled={
                  submittingContract ||
                  isEmpty(currentCustomer) ||
                  isEmpty(currentScooter) ||
                  (!!values.pre_approved && isEmpty(items))
                }
                color="primary"
                fill
              >
                {`${!currentContract ? "Create" : "Update"} Contract `}
                <FontAwesomeIcon icon={faPaperPlane} />
              </Button>
            </div>
            {currentContract && <ButtonSection {...props} values={values} />}
            {failSubmitContract && (
              <Alert color="danger">{failSubmitContract}</Alert>
            )}
          </div>
        );
        const contractInfo = (
          <div className="card mb-3 px-2">
            <div className="mb-3">
              {/* <h4>
                <b>Contract Information</b>
              </h4> */}
              <Form>
                <div className="row align-items-start">
                  <div className="col-md-4 pr-md-1">
                    <Field
                      name="labor_rate"
                      component={TextInput}
                      placeholder="Labor Rate"
                      label="Labor Rate"
                    />
                  </div>
                  <div className="col-md-4 px-md-1">
                    <Field
                      name="warranty"
                      component={TextInput}
                      placeholder="Warranty"
                      label="Warranty"
                    />
                  </div>
                  <div className="col-md-4 pl-md-1">
                    <Field
                      name="key_tag"
                      component={TextInput}
                      placeholder="Key Tag"
                      label="Key Tag"
                    />
                  </div>

                  <div className="col-md-4 pr-md-1">
                    <Field
                      name="repair_order_no"
                      component={TextInput}
                      placeholder="Repair Order #"
                      label="Repair Order #"
                    />
                  </div>

                  <div className="col-md-4 pr-md-1">
                    {currentContract && (
                      <Field
                        name="aasm_state"
                        component={SelectInput}
                        placeholder="Contract State"
                        source={states}
                        label="Contract State"
                      />
                    )}
                    {!currentContract && (
                      <Fragment>
                        <Checkbox
                          number="pre_approved"
                          name="pre_approved"
                          label="Pre-Approved?"
                          checked={values.pre_approved}
                          onChange={onChangeCheckbox({
                            name: "pre_approved",
                            setValues,
                            values
                          })}
                        />
                      </Fragment>
                    )}
                  </div>
                </div>
              </Form>
            </div>
          </div>
        );
        const customerInfo = (
          <div className="card mb-3">
            <div className="content">
              <h4>
                <b>Customer Information</b>
              </h4>
              <Fragment>
                <div className="row">
                  <div className="col-auto">
                    {currentContract && <CustomerButton actionType="update" />}
                    {!currentContract && (
                      <SelectCustomerButton
                        defaultOpen
                        currentCustomer={currentCustomer}
                      />
                    )}
                    {currentCustomer && <Customer customer={currentCustomer} />}
                  </div>
                </div>
                {currentCustomer && (
                  <div>
                    <br />
                    <div className="row">
                      <div className="col-auto">
                        {!currentContract && (
                          <SelectScooterButton
                            defaultOpen
                            currentScooter={currentScooter}
                          />
                        )}
                        {currentContract && (
                          <ScooterButton
                            formAction="update"
                            onSubmitSuccess={setCurrentScooter}
                          >
                            {({ onClick }) => (
                              <div style={{ marginBottom: 10 }}>
                                <Button fill onClick={onClick} color="primary">
                                  Update Scooter{" "}
                                  <FontAwesomeIcon icon={faEdit} />
                                </Button>
                              </div>
                            )}
                          </ScooterButton>
                        )}
                        {currentScooter && <Scooter scooter={currentScooter} />}
                      </div>
                    </div>
                  </div>
                )}
              </Fragment>
            </div>
          </div>
        );
        if (isSmallScreen) {
          return (
            <div className="row">
              <div className="col-12">{customerInfo}</div>
              <div className="col-12">{contractInfo}</div>
              <div className="col-12 mb-3">
                <SecondaryTabs currentContract={currentContract} {...props} />
              </div>
              <div className="col-12 mb-3">
                <PrimaryTabs {...props} />
              </div>
              <div className="col-12">{buttons}</div>
            </div>
          );
        }
        return (
          <>
            <div className="row">
              <div className="col-md-8 pr-md-2 mb-3">
                {contractInfo}
                <SecondaryTabs currentContract={currentContract} {...props} />
                <div className="row col-md-auto pt-md-2">{buttons}</div>
              </div>
              <div className="col-md-4 pl-md-2 mb-3">
                {customerInfo}
                <PrimaryTabs {...props} values={values} />
              </div>
            </div>
          </>
        );
      }}
    />
  );
});

const validationSchema = Yup.object().shape({
  warranty: Yup.string().required("Required"),
  labor_rate: Yup.number()
    .required("Required")
    .min(1, "Must be greater than zero")
    .typeError("Invalid number"),
  repair_order_no: Yup.string()
    .required("Required")
    .min(0, "Must be a positive number")
    .typeError("Invalid number")
});

ContractForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  submittingContract: PropTypes.bool.isRequired,
  failSubmitContract: PropTypes.string,
  doesContinue: PropTypes.bool
};

ContractForm.defaultProps = {
  doesContinue: false
};

export default ContractForm;
